import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../types';
import { NotFoundPage } from '../../components/NotFoundPage';
import { actions } from './slice';
import DocumentLayout from './components/DocumentLayout';
import PdfDocument from './components/PdfDocument';
import { XmlDocument } from './components/XmlDocument/';
import moment from 'moment';
import { Dialog } from '@epo/epods-react-components';
import { actions as searchActions } from '../Search/slice';
import { useNavigate } from 'react-router-dom';
import { SearchResultsObj } from '../Search/components/SearchResults/types';
import { environment } from '../../../utils/constants';

export interface DocumentViewProps {
    publicationNumber: string;
    documentType: string;
    kindCode: string;
    publicationDate?: string;
    setAppParams?: (val: any) => void;
}

export const getItemFromSessionHelper = (
    position,
    documentType,
    setNewPublicationNumber,
    setNewItem,
    setNewData,
    setItemPosition,
    setNewPath,
    sessionValue,
    setLoading,
    setSessionvalue,
) => {
    if (position >= 0) {
        const search = sessionStorage.getItem(`Search_data_${Math.floor(position / 20)}`);
        const document = search ? JSON.parse(search || '') : '';
        let newItemPosition = position - Math.floor(position / 20) * 20;
        if (document && document.content && document.content[newItemPosition]) {
            let path;
            if (documentType === 'pdf' && document.content[newItemPosition].pdfFile) {
                path = document.content[newItemPosition].pdfFile;
            } else {
                path = document.content[newItemPosition].xmlFile;
            }
            setNewPublicationNumber(document.content[newItemPosition].publicationNumber);
            setNewItem(document.content[newItemPosition]);
            setNewData(document);
            setItemPosition(position);
            setNewPath(path);
            if (sessionValue < Math.floor(position / 20)) {
                setLoading(false);
            }
            if (sessionValue > Math.floor(position / 20)) {
                setTimeout(() => {
                    setLoading(false);
                }, 3000);
            }
            setSessionvalue(Math.floor(position / 20));
        } else {
            console.error('Invalid position or document content is undefined');
        }
    }
};

const disabledStyle = {
    pointerEvents: 'none',
    opacity: 0.6,
};

export const updateItemBasedOnUrl = (
    data,
    selectedItem,
    otherKindCodes,
    publicationNumber,
    kindCode,
    item,
    publicationDate,
    setItem,
) => {
    if ((!data || !selectedItem) && otherKindCodes && otherKindCodes.content?.length > 0) {
        let val;
        if (publicationDate) {
            val = otherKindCodes.content.find(
                (i) =>
                    i.publicationNumber.toString() === publicationNumber &&
                    i.publicationKind === kindCode?.toUpperCase() &&
                    i.publicationDate === publicationDate,
            );
        } else {
            val = otherKindCodes.content
                .filter((i) => i.publicationKind === kindCode && i.publicationNumber.toString() === publicationNumber)
                .reduce((a, b) => (a.publicationDate > b.publicationDate ? a : b), {});
        }

        if (val !== item) {
            setItem(val);
        }
    } else if (selectedItem && selectedItem !== item) {
        setItem(selectedItem);
    }
};

const DocumentView = ({
    publicationNumber,
    documentType,
    kindCode,
    publicationDate,
    setAppParams,
}: DocumentViewProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { searchOtherKindCodes } = actions;
    const { searchNext } = searchActions;
    const data = useSelector((state: RootState) => state.searchResults.data);
    const searchFormPayload = useSelector((state: RootState) => state.searchResults.formPayload);
    const otherKindCodes = useSelector((state: RootState) => state.otherKindCodes.data);
    const [pageNotFound, setPageNotFound] = useState(false);
    const [itemPosition, setItemPosition] = useState<number | null>(null);
    const selectedItem = data?.content.find(
        (i) =>
            i.publicationNumber.toString() === publicationNumber &&
            i.publicationKind === kindCode?.toUpperCase() &&
            i.publicationDate === publicationDate,
    );
    const [item, setItem] = useState(selectedItem);
    const [newItem, setNewItem] = useState(selectedItem);
    const [newPublicationNumber, setNewPublicationNumber] = useState();
    const [newPath, setNewPath] = useState<any>('');
    const [newData, setNewData] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [dropdown, setDropdown] = useState<any>();
    const [sessionValue, setSessionvalue] = useState<any>();

    useEffect(() => {
        if (documentType !== 'xml' && documentType !== 'pdf' && !!kindCode) {
            setPageNotFound(true);
        }
    }, [documentType, kindCode]);

    useEffect(() => {
        if ((!data || !selectedItem) && otherKindCodes && otherKindCodes?.content?.length > 0) {
            let val: any;
            if (publicationDate) {
                val = otherKindCodes.content.find(
                    (i) =>
                        i.publicationNumber.toString() === publicationNumber &&
                        i.publicationKind === kindCode?.toUpperCase() &&
                        i.publicationDate === publicationDate,
                );
            } else {
                val = otherKindCodes.content
                    .filter(
                        (i) => i.publicationKind === kindCode && i.publicationNumber.toString() === publicationNumber,
                    )
                    .reduce((a, b) => (a.publicationDate > b.publicationDate ? a : b));
            }
            val !== item && setItem(val);
        } else if (selectedItem && selectedItem !== item) {
            setItem(selectedItem);
        }
    }, [otherKindCodes, selectedItem, data, publicationNumber, kindCode, item, publicationDate]);

    // Gets other kindcodes for the same publication to fill dropdown so it is possible to switch between multiple kind codes
    useEffect(() => {
        const currentWeek = moment().isoWeek();
        const week = moment().day() >= 3 ? currentWeek : currentWeek - 1;
        const payload = {
            publicationNumber:
                (newData || data) && item
                    ? newItem?.publicationNumber /* || item.publicationNumber */
                    : publicationNumber,
            applicationNumber: null,
            ipcSymbol: null,
            publicationDateRange: {
                isRange: true,
                publicationYear: null,
                publicationWeek: null,
                publicationYearStart: 1978,
                publicationWeekStart: 51,
                publicationYearEnd: moment().year(),
                publicationWeekEnd: week,
            },
            publicationKinds: ['A1', 'A2', 'A3', 'A8', 'A9', 'B1', 'B2', 'B3', 'B8', 'B9'],
        };

        (!otherKindCodes ||
            !otherKindCodes.content?.find((i) => i.publicationNumber.toString() === publicationNumber)) &&
            dispatch(searchOtherKindCodes(payload));
    }, [dispatch, searchOtherKindCodes, publicationNumber, data, item, otherKindCodes, newItem, newData]);

    // sets initial position for pagination
    useEffect(() => {
        if (data && itemPosition === null) {
            const initialPosition =
                data.pageable.offset + data.content.findIndex((el) => el.publicationNumber === publicationNumber);
            setItemPosition(initialPosition);
        }
    }, [data, itemPosition, publicationNumber]);

    const handleDocLayout = (url: any) => {
        setDropdown(url);
    };

    useEffect(() => {
        if (!dropdown) {
            let newDocumentype;
            if (newItem) {
                if (documentType === 'pdf' && newItem?.pdfFile) {
                    newDocumentype = 'pdf';
                } else {
                    newDocumentype = 'xml';
                }

                const currentUrl = window.location.pathname;

                const newUrl = `/document/${newDocumentype}/${newItem?.publicationNumber}/${newItem?.publicationKind}/${newItem?.publicationDate}`;

                if (newUrl !== currentUrl.replace('/' + environment.baseUri, '') || documentType !== newDocumentype) {
                    navigate(newUrl);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentType, dropdown, newItem]);

    const setData = (pagination = 0) => {
        const payload = { ...searchFormPayload, page: pagination };
        payload.isCurrent = false;
        dispatch(searchNext(payload));
    };

    useEffect(() => {
        const divElement = document.querySelector('div[data-epo="epo-paginator-30x"]');
        if (divElement instanceof HTMLElement) {
            if (loading) {
                Object.assign(divElement.style, disabledStyle);
            } else {
                divElement.removeAttribute('style');
            }
        }
    }, [loading]);

    const getItemFromSession = (position) => {
        getItemFromSessionHelper(
            position,
            documentType,
            setNewPublicationNumber,
            setNewItem,
            setNewData,
            setItemPosition,
            setNewPath,
            sessionValue,
            setLoading,
            setSessionvalue,
        );
    };

    const onPageChange = (pagination) => {
        setDropdown(null);

        const position = pagination.page;
        setLoading(true);

        if (sessionStorage.getItem(`Search_data_${Math.floor((position || 0) / 20)}`)) {
            getItemFromSession(position);
        } else {
            setData(Math.floor((position || 0) / 20));
            setTimeout(() => {
                getItemFromSession(position);
            }, 3000);
        }
    };

    const handleDocumentLoad = () => {
        setLoading(false);
    };

    if (pageNotFound) {
        return <NotFoundPage />;
    }

    return (
        <>
            {newItem && !dropdown && (
                <DocumentLayout
                    data-testid="document-view"
                    kindCode={kindCode}
                    publicationNumber={newPublicationNumber || publicationNumber}
                    documentType={newItem?.pdfFile ? documentType : 'xml'}
                    itemCount={data ? data.totalElements : 0}
                    item={newItem}
                    onPageChange={onPageChange}
                    handleDocLayout={handleDocLayout}
                    itemPosition={itemPosition}
                    publicationDate={publicationDate}
                >
                    {documentType === 'pdf' && newItem.pdfFile ? (
                        <PdfDocument id={newItem?.id} path={newPath || newItem?.pdfFile} onLoad={handleDocumentLoad} />
                    ) : (
                        <XmlDocument
                            id={newItem?.id}
                            path={newPath !== '' ? newPath : newItem?.xmlFile}
                            onLoad={handleDocumentLoad}
                        />
                    )}
                </DocumentLayout>
            )}

            {item && dropdown && (
                <DocumentLayout
                    data-testid="document-view"
                    kindCode={kindCode}
                    publicationNumber={newPublicationNumber || publicationNumber}
                    documentType={item?.pdfFile ? documentType : 'xml'}
                    itemCount={data ? data.totalElements : 0}
                    item={item}
                    onPageChange={onPageChange}
                    handleDocLayout={handleDocLayout}
                    itemPosition={itemPosition}
                    publicationDate={publicationDate}
                >
                    {documentType === 'pdf' && item.pdfFile ? (
                        <PdfDocument id={item?.id} path={item.pdfFile} onLoad={handleDocumentLoad} />
                    ) : (
                        <XmlDocument id={item?.id} path={item?.xmlFile ?? ''} onLoad={handleDocumentLoad} />
                    )}
                </DocumentLayout>
            )}

            {item && !newItem && !(item && dropdown) && (
                <DocumentLayout
                    data-testid="document-view"
                    kindCode={kindCode}
                    publicationNumber={newPublicationNumber || publicationNumber}
                    documentType={item?.pdfFile ? documentType : 'xml'}
                    itemCount={data ? data.totalElements : 0}
                    item={item}
                    onPageChange={onPageChange}
                    handleDocLayout={handleDocLayout}
                    itemPosition={itemPosition}
                    publicationDate={publicationDate}
                >
                    {documentType === 'pdf' && item.pdfFile ? (
                        <PdfDocument id={item?.id} path={item.pdfFile} onLoad={handleDocumentLoad} />
                    ) : (
                        <XmlDocument id={item?.id} path={item?.xmlFile ?? ''} onLoad={handleDocumentLoad} />
                    )}
                </DocumentLayout>
            )}
            <Dialog />
        </>
    );
};

export default DocumentView;
